/**
 * Hook TanStack Query per il portale job Prefect.
 */

import {
  useMutation,
  useQueryClient,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  fetchFlowRuns,
  fetchJobRuns,
  fetchFlowRunLogs,
  fetchManualScanStatus,
  runDeployment,
  startManualScan,
  type ManualScanStatus,
} from './api.js';
import type { FlowRun, JobRun, LogLine } from './types.js';

/** Intervallo di live polling (ms) per la vista /jobs e i log dei job in corso. */
const POLL_INTERVAL_MS = 2000;

/** Chiavi query centralizzate per il dominio Prefect. */
export const prefectKeys = {
  flowRuns: () => ['prefect', 'flow-runs'] as const,
  jobRuns: () => ['prefect', 'job-runs'] as const,
  logs: (id: string) => ['prefect', 'logs', id] as const,
  manualScan: () => ['prefect', 'manual-scan'] as const,
} as const;

/** Polling dello stato della scansione manuale: lento a riposo, rapido mentre è in corso. */
const MANUAL_SCAN_IDLE_POLL_MS = 60_000;
const MANUAL_SCAN_ACTIVE_POLL_MS = 5_000;

/**
 * Recupera la lista degli ultimi flow-run Prefect.
 */
export function usePrefectFlowRuns(): UseQueryResult<FlowRun[]> {
  return useQuery({
    queryKey: prefectKeys.flowRuns(),
    queryFn: () => fetchFlowRuns(50),
  });
}

/**
 * Vista /jobs: per job l'ultima esecuzione (in corso/terminata) + i job mai eseguiti.
 * Esegue live polling ogni 2s per individuare i job attualmente in corso.
 */
export function usePrefectJobRuns(): UseQueryResult<JobRun[]> {
  return useQuery({
    queryKey: prefectKeys.jobRuns(),
    queryFn: () => fetchJobRuns(),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

/**
 * Recupera le righe di log di un flow-run.
 * La query e abilitata solo quando `id` è non-null.
 * Per i job in corso (`poll=true`) la query esegue live polling ogni 2s.
 */
export function usePrefectFlowRunLogs(id: string | null, poll = false): UseQueryResult<LogLine[]> {
  return useQuery({
    queryKey: prefectKeys.logs(id ?? ''),
    queryFn: () => fetchFlowRunLogs(id!, 200),
    enabled: id !== null,
    refetchInterval: poll ? POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  });
}

/**
 * Avvia un'esecuzione on-demand di un deployment Prefect (solo amministratori).
 * Al successo invalida la lista job-runs cosi il nuovo run compare.
 */
export function useRunDeployment(): UseMutationResult<{ id: string }, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deploymentId: string) => runDeployment(deploymentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: prefectKeys.flowRuns() });
      void queryClient.invalidateQueries({ queryKey: prefectKeys.jobRuns() });
    },
  });
}

/**
 * Stato della scansione vulnerabilità manuale (pulsante header): deployment disponibile e run
 * attivo. Polling ogni 5s mentre un run è attivo, ogni minuto altrimenti.
 */
export function useManualScanStatus(enabled = true): UseQueryResult<ManualScanStatus> {
  return useQuery({
    queryKey: prefectKeys.manualScan(),
    queryFn: fetchManualScanStatus,
    enabled,
    refetchInterval: (query) =>
      query.state.data?.activeRun ? MANUAL_SCAN_ACTIVE_POLL_MS : MANUAL_SCAN_IDLE_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

/** Avvia la scansione vulnerabilità manuale (solo amministratori); aggiorna stato e /jobs. */
export function useStartManualScan(): UseMutationResult<{ id: string }, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => startManualScan(),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: prefectKeys.manualScan() });
      void queryClient.invalidateQueries({ queryKey: prefectKeys.jobRuns() });
    },
  });
}
